/**
 * Reusable Practice Area Select Component
 * Uses react-select-async-paginate for efficient loading and pagination
 * Supports search and infinite scroll
 * Styled to match PrimeReact Dropdown components
 */

import React, { useMemo } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadPracticeAreaOptions } from 'hooks/queries/usePracticeAreasPaginated';
import '../shared/AsyncSelect.scss';

/**
 * PracticeAreaSelect Component
 *
 * @param {Object} props - Component props
 * @param {string|Object|null} props.value - Selected value (can be ID string, full option object, or null)
 * @param {Function} props.onChange - Change handler (value, actionMeta) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isClearable - Show clear button
 * @param {boolean} props.isDisabled - Disable the select
 * @param {boolean} props.isSearchable - Enable search (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.status - Filter by status (default: 'active')
 * @param {string} props.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} props.pageSize - Items per page (default: 20)
 * @param {Object} props.styles - Custom react-select styles
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input ID attribute
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @returns {JSX.Element}
 */
function PracticeAreaSelect({
  value = null,
  onChange,
  placeholder = 'Select practice area',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  className = '',
  status = 'active',
  sort_order = 'alphabet_ascending',
  pageSize = 20,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  // Memoize the load options function with config
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadPracticeAreaOptions(search, loadedOptions, additional, {
        pageSize,
        status,
        sort_order,
      }),
    [pageSize, status, sort_order]
  );

  // Debounce time for search input (in milliseconds)
  const debounceTimeout = 300;

  // Additional props for AsyncPaginate
  const additional = {
    nextToken: '',
  };

  // Merge custom styles with menu styling to prevent ResizeObserver issues
  const mergedStyles = useMemo(
    () => ({
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      ...styles,
    }),
    [styles]
  );

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      className={className}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      additional={additional}
      debounceTimeout={debounceTimeout}
      // cacheUniqs={[status, sort_order]} // Re-fetch when these change
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading practice areas...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No practice areas found for "${inputValue}"` : 'No practice areas available')}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

export default PracticeAreaSelect;
